import { useCallback } from 'react'
import { AddressFragment, useCountriesLazyQuery, useMunicipalitiesLazyQuery } from 'src/api/api.generated'

export type FetchAddressParams = {
  countryIsoCode: string | null | undefined
  address: string | null | undefined
  city: string | null | undefined
  municipalityIstat: string | null | undefined
  municipalityProvince: string | null | undefined
  municipalityName: string | null | undefined
  postalCode: string | null | undefined
}

export default function useFetchAddress() {
  const [countriesQuery] = useCountriesLazyQuery()
  const [municipalitiesQuery] = useMunicipalitiesLazyQuery()
  const fetchAddress = useCallback(
    async (params: FetchAddressParams): Promise<AddressFragment> => {
      const addressFragment: AddressFragment = {
        address: params.address,
        city: params.city,
        postalCode: params.postalCode,
      }

      const countryIsoCode = params.countryIsoCode || 'IT'

      const countriesResult = await countriesQuery({
        variables: {
          filter: {
            isoCode: { eq: countryIsoCode },
          },
        },
      })
      const country = countriesResult.data?.countries?.find((c) => c.isoCode === countryIsoCode)

      addressFragment.countryId = country?.id

      if (country?.isoCode === 'IT' && params.municipalityIstat) {
        const municipalitiesResult = await municipalitiesQuery({
          variables: {
            filter: {
              istat: {
                eq: params.municipalityIstat,
              },
            },
            limit: 1,
          },
        })
        const municipality = municipalitiesResult.data?.municipalities?.[0]
        if (municipality) {
          addressFragment.municipalityId = municipality.id
          addressFragment.province = municipality.province
        }
        addressFragment.postalCode = params.postalCode || municipality?.postalCode
      }

      return addressFragment
    },
    [countriesQuery, municipalitiesQuery],
  )

  return fetchAddress
}
